import { useScrollReveal } from "@/hooks/useScrollReveal";

const AI_FEATURES = [
  {
    icon: "ri-brain-line",
    color: "bg-gradient-to-br from-primary-500 to-accent-500",
    title: "Copiloto IA de convivencia escolar",
    desc: "Asistente inteligente que responde consultas sobre normativa, protocolos de actuación, reglamentos internos y marcos legales en lenguaje natural.",
    highlights: ["Consultas en lenguaje natural", "Respuestas con cita normativa", "Contexto institucional"],
  },
  {
    icon: "ri-file-chart-line",
    color: "bg-gradient-to-br from-accent-500 to-amber-500",
    title: "Generación automática de informes",
    desc: "Elabora informes para tribunal, reportes de convivencia y resúmenes ejecutivos automáticamente desde los datos registrados en la plataforma.",
    highlights: ["Informes para tribunal", "Reportes de convivencia", "Exportación a PDF"],
  },
  {
    icon: "ri-notification-4-line",
    color: "bg-gradient-to-br from-red-400 to-accent-500",
    title: "Alertas predictivas de riesgo",
    desc: "La IA analiza patrones y anticipa situaciones críticas antes de que escalen: casos sin seguimiento, plazos judiciales en riesgo, reincidencias.",
    highlights: ["Detección temprana de riesgos", "Recomendaciones accionables", "Notificaciones al equipo"],
  },
  {
    icon: "ri-dashboard-3-line",
    color: "bg-gradient-to-br from-secondary-500 to-primary-500",
    title: "Análisis de clima escolar",
    desc: "Dashboard con indicadores generados por IA: tendencias de convivencia, carga de trabajo del equipo, efectividad de intervenciones.",
    highlights: ["KPIs de convivencia", "Comparativas mensuales", "Proyecciones trimestrales"],
  },
];

const SAMPLE_QUESTIONS = [
  "¿Cuál es el protocolo ante una denuncia de acoso escolar?",
  "¿Qué debo hacer si un estudiante revela una vulneración de derechos?",
  "¿Cuáles son los plazos legales para informar al tribunal de familia?",
  "Muéstrame el flujo de actuación para casos de ciberacoso",
];

export default function IAInstitucionalSection() {
  const sectionRef = useScrollReveal<HTMLElement>({ threshold: 0.12, selector: ".scroll-reveal", staggerDelay: 100 });

  return (
    <section id="ia-institucional" className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-background-300/60 to-transparent" />

      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-primary-100/40 via-accent-100/30 to-secondary-100/30 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="scroll-reveal text-xs font-semibold text-white tracking-widest uppercase bg-gradient-to-r from-primary-600 to-accent-500 px-3 py-1 rounded-full inline-block">
            Inteligencia Artificial
          </span>
          <h2 className="scroll-reveal text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mt-4 tracking-tight">
            IA al servicio de la convivencia escolar
          </h2>
          <p className="scroll-reveal text-lg text-foreground-500 mt-4 max-w-2xl mx-auto leading-relaxed">
            Un copiloto inteligente que analiza normativa, genera informes, anticipa riesgos y responde consultas sobre protocolos de actuación en segundos.
          </p>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {AI_FEATURES.map((feature, i) => (
            <div key={feature.title} className="scroll-reveal group bg-white rounded-3xl border border-background-200/80 shadow-soft p-6 md:p-8 hover:shadow-soft-lg transition-all duration-300 cursor-default">
              <div className={`w-12 h-12 rounded-2xl ${feature.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${feature.icon} text-white text-xl`} />
              </div>
              <h3 className="text-lg font-bold text-foreground-950 mb-2">{feature.title}</h3>
              <p className="text-sm text-foreground-500 leading-relaxed mb-4">{feature.desc}</p>
              <div className="flex flex-wrap gap-2">
                {feature.highlights.map((h) => (
                  <span key={h} className="px-2.5 py-1 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg border border-background-200/60">
                    {h}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Interactive demo: AI chat mockup */}
        <div className="scroll-reveal">
          <div className="bg-white rounded-3xl border border-background-200/80 shadow-soft-lg overflow-hidden">
            <div className="bg-gradient-to-r from-primary-600 via-primary-500 to-accent-500 px-6 py-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <i className="ri-brain-line text-white text-sm" />
              </div>
              <div>
                <p className="text-sm font-bold text-white">Copiloto de Convivencia</p>
                <p className="text-xs text-white/70">Asistente IA normativo &middot; En línea</p>
              </div>
            </div>

            <div className="p-6">
              <p className="text-xs text-foreground-400 mb-4">Prueba algunas consultas frecuentes:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5">
                {SAMPLE_QUESTIONS.map((q) => (
                  <div key={q} className="group px-4 py-3 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200 hover:bg-primary-50/30 cursor-pointer transition-all duration-200">
                    <p className="text-sm text-foreground-600 group-hover:text-foreground-900 flex items-start gap-2">
                      <i className="ri-chat-3-line text-primary-400 text-xs mt-0.5 flex-shrink-0" />
                      {q}
                    </p>
                  </div>
                ))}
              </div>

              {/* Simulated AI response */}
              <div className="bg-primary-50/50 rounded-2xl p-5 border border-primary-100/50">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-2-line text-white text-xs" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-primary-700 mb-2">Copiloto de Convivencia</p>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      Según el <strong>Reglamento Interno de Convivencia Escolar</strong> (art. 28), ante una denuncia de acoso escolar se debe activar el protocolo en un plazo máximo de <strong>24 horas</strong>. Los pasos son: (1) recepción formal de la denuncia, (2) comunicación inmediata a la Dirección, (3) adopción de medidas cautelares si corresponde, (4) inicio de investigación en máximo 5 días hábiles, y (5) informe de conclusiones y medidas en un plazo no superior a 15 días hábiles.
                    </p>
                    <p className="text-xs text-foreground-400 mt-3 flex items-center gap-1.5">
                      <i className="ri-link" />
                      Fuente: RICE 2025, Título IV, Art. 28-32 &middot; Ley 20.536 sobre Violencia Escolar
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}